import React from 'react';
import { Link, useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { PageHeader, SectionCard, StatusPill, ProjectTabBar } from '@/components/portal/PortalUI';
import { Button } from '@shared/components/ui/button';
import { supabase } from '@shared/supabaseClient';
import { Upload, Users, FileText, ArrowLeft, Calendar, Building2, CheckCircle, XCircle, Clock, Trash2, Loader2, Send, ShieldAlert, ClipboardCheck, ClipboardList } from 'lucide-react';
import { format } from 'date-fns';
import { UploadDocumentDialog } from '@/components/portal/UploadDocumentDialog';
import { InviteTeamMemberDialog } from '@/components/portal/InviteTeamMemberDialog';
import { useToast } from '@shared/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";

const PortalProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { session } = useOutletContext();
  const { toast } = useToast();
  const [project, setProject] = React.useState(null);
  const [documents, setDocuments] = React.useState([]);
  const [teamMembers, setTeamMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [deletingMemberId, setDeletingMemberId] = React.useState(null);
  const [confirmDeleteMember, setConfirmDeleteMember] = React.useState(null);
  const [showSubmitSuccess, setShowSubmitSuccess] = React.useState(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = React.useState('');
  const [teamInductionStatus, setTeamInductionStatus] = React.useState({});
  const [leadInductionStatus, setLeadInductionStatus] = React.useState({ total: 0, submitted: 0, allSubmitted: false, loading: true });

  React.useEffect(() => {
    loadProjectData();
  }, [projectId]);

  const loadProjectData = async () => {
    try {
      // Load project details
      const { data: projectData } = await supabase
        .from('projects')
        .select(`
          *,
          depots:site_id (id, name, code, address)
        `)
        .eq('id', projectId)
        .single();

      setProject(projectData);

      // Load documents
      const { data: docsData } = await supabase
        .from('project_documents')
        .select('*')
        .eq('project_id', projectId)
        .order('uploaded_at', { ascending: false });

      setDocuments(docsData || []);

      // Load team members
      const { data: teamData } = await supabase
        .from('project_team_members')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      setTeamMembers(teamData || []);

      // Check project lead's own induction status
      if (projectData && session?.person?.email) {
        const leadEmail = session.person.email.toLowerCase();
        const { data: leadSubs } = await supabase
          .from('induction_submissions')
          .select('id, status, brief_id')
          .eq('person_email', leadEmail)
          .eq('depot_id', projectData.site_id)
          .in('status', ['invited', 'in_progress', 'submitted', 'approved', 'pending_acknowledgement']);

        // Deduplicate by brief_id — keep the most advanced status per brief.
        // Without this, multiple submissions for the same brief (e.g. from
        // previous projects at the same site) inflate the total count and
        // incorrectly show "Site Induction Required" even when complete.
        const STATUS_RANK = { approved: 4, submitted: 3, pending_acknowledgement: 2, in_progress: 1, invited: 0 };
        const bestPerBrief = new Map();
        for (const sub of leadSubs || []) {
          const existing = bestPerBrief.get(sub.brief_id);
          if (!existing || (STATUS_RANK[sub.status] ?? -1) > (STATUS_RANK[existing.status] ?? -1)) {
            bestPerBrief.set(sub.brief_id, sub);
          }
        }
        const deduped = Array.from(bestPerBrief.values());
        const total = deduped.length;
        // pending_acknowledgement counts as complete — it only needs a click to acknowledge,
        // not a full re-induction, same as the returning-worker flow in staff_create_invite.
        const submitted = deduped.filter(s =>
          s.status === 'submitted' || s.status === 'approved' || s.status === 'pending_acknowledgement'
        ).length;
        setLeadInductionStatus({
          total,
          submitted,
          allSubmitted: total > 0 && submitted === total,
          loading: false,
        });
      } else {
        setLeadInductionStatus(prev => ({ ...prev, loading: false }));
      }

      // Check induction completion for each team member
      if (teamData && teamData.length > 0 && projectData) {
        const statusMap = {};
        for (const member of teamData) {
          // Check if all of the project lead's briefs are complete for this member
          const { data: memberSubs } = await supabase
            .from('induction_submissions')
            .select('id, status, brief_id')
            .eq('person_email', member.person_email)
            .eq('depot_id', projectData.site_id)
            .in('status', ['invited', 'in_progress', 'submitted', 'approved']);

          const total = memberSubs?.length || 0;
          const completed = memberSubs?.filter(s => s.status === 'approved' || s.status === 'submitted').length || 0;
          statusMap[member.id] = { total, completed, allDone: total > 0 && completed === total };
        }
        setTeamInductionStatus(statusMap);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  const allTeamMembersComplete = () => {
    if (teamMembers.length === 0) return false;
    return teamMembers.every(m => {
      const status = teamInductionStatus[m.id];
      return status && status.allDone;
    });
  };

  const isSubmittableStatus = () => {
    return project?.status === 'draft_in_progress' || project?.status === 'draft_pending_supplier' || project?.status === 'submitted_awaiting_approval';
  };

  const isResubmission = () => {
    return project?.status === 'submitted_awaiting_approval';
  };

  const canSubmitForReview = () => {
    if (!project) return false;
    if (!isSubmittableStatus()) return false;
    // Project lead must have completed their own induction
    if (!leadInductionComplete) return false;
    // All team members must have completed their inductions
    return allTeamMembersComplete();
  };

  const handleSubmitForReview = async () => {
    if (!canSubmitForReview()) return;
    setSubmitting(true);
    const isUpdate = isResubmission();
    try {
      const updatePayload = {
        submitted_at: new Date().toISOString(),
        submitted_by_email: session?.person?.email || session?.email,
      };
      // Only set status if not already submitted
      if (!isUpdate) {
        updatePayload.status = 'submitted_awaiting_approval';
      }

      const { error } = await supabase
        .from('projects')
        .update(updatePayload)
        .eq('id', projectId);

      if (error) throw error;

      // Send email notification to the admin who created the project
      if (project.created_by_admin_email) {
        try {
          const notifyRes = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-project-submitted`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              project_id: projectId,
              project_name: project.project_name,
              admin_email: project.created_by_admin_email,
              lead_name: project.project_lead_name,
              lead_email: project.project_lead_email,
              site_name: project.depots?.name || 'Unknown site',
              is_update: isUpdate,
            }),
          });
          const notifyData = await notifyRes.json();
          if (!notifyData.ok) console.warn('Notification email failed:', notifyData);
        } catch (emailErr) {
          console.warn('Failed to send project submission notification:', emailErr);
        }
      }

      // Show prominent success dialog
      setSubmitSuccessMessage(
        isUpdate
          ? 'Your project updates have been resubmitted. The site administrator has been notified of the changes.'
          : 'Your project has been submitted for review. The site administrator will review your documents and team details. You will be notified once a decision is made.'
      );
      setShowSubmitSuccess(true);
      loadProjectData();
    } catch (error) {
      console.error('Error submitting project:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit project for review. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeamMember = async (member) => {
    setDeletingMemberId(member.id);
    try {
      const { data, error } = await supabase.rpc('portal_remove_team_member', {
        p_member_id:       member.id,
        p_project_id:      project.id,
        p_requester_email: session?.person?.email,
      });

      if (error) throw error;
      if (data?.success === false) throw new Error(data.error || 'Failed to remove team member');

      toast({
        title: 'Team Member Removed',
        description: `${member.person_name} has been removed from the project.`,
      });
      loadProjectData();
    } catch (error) {
      console.error('Error deleting team member:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove team member. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setDeletingMemberId(null);
      setConfirmDeleteMember(null);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      draft_pending_supplier: { label: 'Awaiting Setup', tone: 'info' },
      draft_in_progress: { label: 'In Progress', tone: 'warning' },
      submitted_awaiting_approval: { label: 'Under Review', tone: 'info' },
      approved: { label: 'Approved', tone: 'success' },
      rejected: { label: 'Rejected', tone: 'destructive' },
      active: { label: 'Active', tone: 'success' },
      completed: { label: 'Completed', tone: 'default' },
      archived: { label: 'Archived', tone: 'default' }
    };
    return statusMap[status] || { label: status, tone: 'default' };
  };

  const getDocumentStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'Pending Review', tone: 'warning', icon: Clock },
      accepted: { label: 'Accepted', tone: 'success', icon: CheckCircle },
      rejected: { label: 'Rejected', tone: 'destructive', icon: XCircle }
    };
    return statusMap[status] || { label: status, tone: 'default', icon: FileText };
  };

  const isProjectLead = project && session?.person?.email?.toLowerCase() === project.project_lead_email?.toLowerCase();
  const leadInductionComplete = leadInductionStatus.allSubmitted;
  // Team members can be added any time by the project lead — no induction prerequisite.
  // Induction completion is only required for submitting the project for review.
  const canManageProject = isProjectLead;

  // ── Auto-redirect to Setup Guide if project has outstanding steps ─────────
  // Projects that are not yet approved/completed/archived still have work to do
  // — send the user to the setup guide so they see exactly what's needed.
  const projectFullyComplete = ['approved', 'completed', 'archived'].includes(project?.status);
  React.useEffect(() => {
    if (!loading && project && !projectFullyComplete) {
      navigate(`/portal/projects/${projectId}/setup`, { replace: true });
    }
  }, [loading, project, projectFullyComplete, projectId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">Project not found</p>
          <Button onClick={() => navigate('/portal/inductions')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(project.status);
  const acceptedDocs = documents.filter(d => d.status === 'accepted').length;
  const pendingDocs = documents.filter(d => d.status === 'pending').length;
  const teamWithPasses = teamMembers.filter(m => m.pass_id).length;

  return (
    <div className="space-y-6">
      <ProjectTabBar projectId={projectId} isProjectLead={isProjectLead} />
      <PageHeader
        title={project.project_name}
        subtitle={project.project_summary || 'Project details and team management'}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/portal/inductions')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/portal/projects/${projectId}/setup`)} title="View setup checklist for this project">
              <ClipboardList className="h-4 w-4 mr-2" />
              Setup Guide
            </Button>
            {canManageProject && canSubmitForReview() && (
              <Button size="sm" onClick={handleSubmitForReview} disabled={submitting}>
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                ) : isResubmission() ? (
                  <><Send className="h-4 w-4 mr-2" />Update &amp; Resubmit</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />Submit for Review</>
                )}
              </Button>
            )}
            {canManageProject && isSubmittableStatus() && !isResubmission() && !canSubmitForReview() && teamMembers.length > 0 && (
              <Button size="sm" variant="outline" disabled title="All team members must complete inductions">
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            )}
          </div>
        }
      />

      {/* Induction Gate Banner */}
      {isProjectLead && !leadInductionStatus.loading && !leadInductionComplete && leadInductionStatus.total > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-800">Site Induction Required</h3>
              <p className="text-sm text-amber-700 mt-1">
                You must complete your site induction before you can manage this project.
                You have completed {leadInductionStatus.submitted} of {leadInductionStatus.total} induction{leadInductionStatus.total !== 1 ? 's' : ''}.
              </p>
              <Button
                size="sm"
                className="mt-3 bg-amber-600 hover:bg-amber-700"
                onClick={() => navigate('/portal/inductions')}
              >
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Go to Inductions
              </Button>
            </div>
          </div>
        </div>
      )}

      {isProjectLead && !leadInductionStatus.loading && leadInductionStatus.total === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Induction Setup Pending</h3>
              <p className="text-sm text-gray-600 mt-1">
                Your site induction is being set up. Please check back shortly or visit the Inductions tab.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Project Overview */}
      <SectionCard 
        title="Project Overview" 
        subtitle={project.depots?.name || 'Site information'}
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <StatusPill tone={statusInfo.tone} label={statusInfo.label} />
            {project.rejected_at && (
              <div className="text-sm text-destructive">
                Rejected: {project.rejection_reason}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start gap-2">
              <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="font-medium">Site</div>
                <div className="text-muted-foreground">{project.depots?.name}</div>
                {project.depots?.address && (
                  <div className="text-xs text-muted-foreground mt-1">{project.depots.address}</div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="font-medium">Timeline</div>
                <div className="text-muted-foreground">
                  {format(new Date(project.estimated_start_date), 'MMM d, yyyy')} - {format(new Date(project.estimated_end_date), 'MMM d, yyyy')}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="font-medium">Documents</div>
                <div className="text-muted-foreground">
                  {acceptedDocs} accepted, {pendingDocs} pending review
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Users className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="font-medium">Team</div>
                <div className="text-muted-foreground">
                  {teamMembers.length} invited, {teamWithPasses} with passes
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Documents & Team Grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Documents Card */}
        <SectionCard 
          title="RAMS Documents" 
          subtitle="Upload and track review status"
        >
          {documents.length === 0 ? (
            <div className="text-center py-8">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-4">No documents uploaded yet</p>
              {canManageProject && (
                <Button size="sm" onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => {
                const docStatus = getDocumentStatusInfo(doc.status);
                const DocIcon = docStatus.icon;
                return (
                  <div key={doc.id} className="flex items-start justify-between p-3 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <p className="text-sm font-medium truncate">{doc.file_name}</p>
                        {doc.version > 1 && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            v{doc.version}
                          </span>
                        )}
                        {!doc.is_current_version && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            Old Version
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="uppercase">{doc.document_type}</span>
                        <span>•</span>
                        <span>{format(new Date(doc.uploaded_at), 'MMM d')}</span>
                        {doc.version_notes && (
                          <>
                            <span>•</span>
                            <span className="italic">{doc.version_notes}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <DocIcon className="h-4 w-4" />
                      <StatusPill tone={docStatus.tone} label={docStatus.label} />
                    </div>
                  </div>
                );
              })}
              {canManageProject && (
                <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </div>
          )}
        </SectionCard>

        {/* Team Card */}
        <SectionCard 
          title="Project Team" 
          subtitle="Invite members and track inductions"
        >
          {teamMembers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-4">{canManageProject ? 'No team members added yet' : 'No team members yet'}</p>
              {canManageProject && (
                <Button size="sm" onClick={() => setInviteDialogOpen(true)}>
                  <Users className="h-4 w-4 mr-2" />
                  Invite Team Member
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {teamMembers.map(member => {
                const inductionInfo = teamInductionStatus[member.id];
                const isDeleting = deletingMemberId === member.id;
                return (
                  <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{member.person_name}</p>
                      <p className="text-xs text-muted-foreground">{member.person_email}</p>
                      {member.person_role && (
                        <p className="text-xs text-muted-foreground mt-1">{member.person_role}</p>
                      )}
                      {inductionInfo && (
                        <p className="text-xs mt-1">
                          <span className={inductionInfo.allDone ? 'text-green-600' : 'text-amber-600'}>
                            Inductions: {inductionInfo.completed}/{inductionInfo.total} complete
                          </span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill 
                        tone={member.pass_id ? 'success' : inductionInfo?.allDone ? 'warning' : 'default'} 
                        label={member.pass_id ? 'Pass Issued' : inductionInfo?.allDone ? 'Complete' : 'In Progress'} 
                      />
                      {canManageProject && project?.status !== 'approved' && project?.status !== 'active' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setConfirmDeleteMember(member)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {canManageProject && (
                <Button size="sm" className="w-full mt-2" onClick={() => setInviteDialogOpen(true)}>
                  <Users className="h-4 w-4 mr-2" />
                  Invite Team Member
                </Button>
              )}
              {!allTeamMembersComplete() && teamMembers.length > 0 && (
                <p className="text-xs text-amber-600 text-center mt-2">
                  All team members must complete their inductions before you can submit for review.
                </p>
              )}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Dialogs */}
      <UploadDocumentDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        projectId={projectId}
        onSuccess={(document) => {
          loadProjectData();
          toast({
            title: 'Document uploaded',
            description: `${document.file_name} has been uploaded successfully and is pending review.`,
          });
        }}
      />

      <InviteTeamMemberDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        projectId={projectId}
        onSuccess={(teamMember) => {
          loadProjectData();
          toast({
            title: 'Invitation sent',
            description: `${teamMember.person_name} has been sent a portal invitation.`,
          });
        }}
        onDone={() => navigate(`/portal/projects/${projectId}/setup`)}
      />

      {/* Delete Team Member Confirmation */}
      <Dialog open={!!confirmDeleteMember} onOpenChange={(open) => !open && setConfirmDeleteMember(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{confirmDeleteMember?.person_name}</strong> from this project?
              This will also delete their induction submissions for this site. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteMember(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleDeleteTeamMember(confirmDeleteMember)}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submission Success Dialog */}
      <Dialog open={showSubmitSuccess} onOpenChange={(open) => { if (!open) { setShowSubmitSuccess(false); navigate(`/portal/projects/${projectId}/setup`); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <DialogTitle className="text-center text-xl">
              {isResubmission() ? 'Project Updated!' : 'Project Submitted!'}
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              {submitSuccessMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center pt-2">
            <Button onClick={() => { setShowSubmitSuccess(false); navigate(`/portal/projects/${projectId}/setup`); }} className="px-8">
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalProjectDetail;
